import { SpinnerIcon } from 'components/icons'
import { TableBody } from 'components/ui/table/table-body'
import TableEmpty from 'components/ui/table/table-empty'
import { TableHeader } from 'components/ui/table/table-header'
import { TableConsumer, TableProvider } from 'context/table-context'
import classNames from 'helpers/class-names'
import { HTMLAttributes, ReactNode, forwardRef } from 'react'
import {
  ListRange,
  TableComponents,
  TableVirtuoso,
  TableVirtuosoHandle,
} from 'react-virtuoso'

type BaseProps = HTMLAttributes<HTMLTableElement> & {
  wrapperClassName?: string
  emptyButtonLabel?: string
  emptyButtonText?: string
  onClickEmptyButton?: () => void
  isLoading?: boolean
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  tabIndex?: number
}

type VirtualizedProps = {
  virtualized: true
  children: (index: number, data: any) => ReactNode
  header: ReactNode
  footer?: ReactNode
  data: Array<any>
  row: TableComponents['TableRow']
  rangeChanged?: (range: ListRange) => void
}

type NonVirtualizedProps = {
  virtualized?: false
  children: ReactNode
  data?: undefined
  header?: undefined
  row?: undefined
  footer?: undefined
}

export const Table = forwardRef<
  TableVirtuosoHandle,
  BaseProps & (VirtualizedProps | NonVirtualizedProps)
>(
  (
    {
      children,
      wrapperClassName,
      emptyButtonLabel,
      emptyButtonText,
      onClickEmptyButton,
      virtualized = false,
      data,
      header,
      row,
      isLoading,
      onKeyDown,
      tabIndex,
      ...props
    },
    ref,
  ) => (
    <TableProvider>
      <TableConsumer>
        {({ footer = null, isEmpty }) => (
          <>
            <div
              tabIndex={tabIndex}
              onKeyDown={onKeyDown}
              className={classNames(
                'tracking-normal',
                { 'h-full w-full': virtualized },
                wrapperClassName,
                { 'outline-none': typeof tabIndex !== 'undefined' },
              )}>
              {!virtualized && (
                <table
                  {...props}
                  className={classNames(
                    'w-full table-fixed text-xs font-bold tracking-normal text-gray-400',
                    props.className,
                  )}>
                  {children}
                </table>
              )}
              {virtualized && (
                <div
                  className={classNames('relative z-0 h-full w-full', {
                    'h-8': !data?.length,
                  })}>
                  {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-60 backdrop-blur-sm">
                      <SpinnerIcon className="mx-auto size-10 animate-spin text-gray-200" />
                    </div>
                  )}
                  <TableVirtuoso
                    ref={ref as any}
                    style={{ height: '100%' }}
                    rangeChanged={(props as VirtualizedProps).rangeChanged}
                    components={{
                      Table: ({ children, ...props }) => (
                        <table
                          className="w-full table-fixed text-xs font-bold tracking-normal text-gray-400"
                          {...props}>
                          {children}
                        </table>
                      ),
                      TableBody: TableBody,
                      TableRow: row,
                      TableHead: TableHeader,
                    }}
                    data={data}
                    fixedHeaderContent={() => header}
                    itemContent={children as VirtualizedProps['children']}
                  />
                </div>
              )}
              {((!virtualized && isEmpty) ||
                (virtualized && !data?.length)) && (
                <TableEmpty
                  {...{ emptyButtonLabel, emptyButtonText, onClickEmptyButton }}
                />
              )}
            </div>
            {props.footer}
            {footer}
          </>
        )}
      </TableConsumer>
    </TableProvider>
  ),
)
